// dependencies
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import _last from 'lodash/last'
import _isEmpty from 'lodash/isEmpty'
import { useStaticQuery, graphql } from 'gatsby'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { saveLocalStorage, getFromBrowserStorage, removeLocalStorage } from '@helpers/storage'
import moment from 'moment'
// helpers
import { getNearestStore, groupStoresByAddress } from '@helpers/store-locator'
import { getCurrentLocation, getRegionZone } from '@helpers/geo-location'
import { useStore } from '@hooks/useStore'
import { setMyStore } from '@redux/modules/my-store'
import { getStoreInfo, getAllStore } from '@services/stores'
import { StoreLocatorProvider } from '@context/storeLocatorContext/provider'
// components
import MyStoreModalLocator from '@components/store-locator-v2/my-store/MyStoreModalLocator'

export const FETCH_PROPERTIES = {
  ALL_STORES: 'ALL_STORES',
}

export const SESSION_MY_STORE_STRING = 'my_preferred_store'

/**
 * @param {Args} store
 * @returns {void}
 */
export function useMyStore(_, fetchProperties) {
  const dispatch = useDispatch()
  const { myStore: myStoreContext } = useSelector(state => state.myStore)
  // GET CURRENT LOCATION
  const rtg_location = getCurrentLocation()
  const { lat, long } = getRegionZone(rtg_location)
  const strapiStoreData = useStaticQuery(query)
  const [allStrapiStores, setAllStrapiStores] = useState([])
  const [allStores, setAllStores] = useState([])
  const [strapiStoresLoadedFlag, setStrapiStoresLoadedFlag] = useState(false)
  const [mapUiDrawerIsOpen, setMapUiDrawerIsOpen] = useState(false)

  const myStore = useStore({ store: myStoreContext })

  const groupedStrapiStores = useMemo(() => groupStoresByAddress(allStrapiStores), [allStrapiStores])

  const relatedStores = useMemo(() => {
    const myStoreGrouped = groupedStrapiStores?.find(
      store => +(store?.storeNumber || store?.StoreNumber) === +myStore?.storeNumber,
    )
    return [{ storeNumber: myStoreGrouped?.storeNumber }, ...(myStoreGrouped?.types || [])]
  }, [groupedStrapiStores, myStore])

  const setMyStoreData = useCallback(
    myStoreData => {
      const storeNumber = myStoreData?.storeNumber || myStoreData?.StoreNumber
      dispatch(setMyStore(myStoreData))

      const minutesFromLastUpdate = moment.duration(moment().diff(myStoreData?.createdAt)).asMinutes()
      if (!myStoreData?.createdAt || !minutesFromLastUpdate || minutesFromLastUpdate > 60) {
        if (storeNumber)
          getStoreInfo(storeNumber).then(store => {
            dispatch(setMyStore({ ...myStoreData, ...store, createdAt: new Date().getTime() }))
          })
      }
    },
    [dispatch],
  )

  const getMyStore = useCallback(() => {
    const myStoreData = getNearestStore({ latitude: lat, longitude: long }, strapiStoreData?.allStrapiStore?.edges)
    if (myStoreData?.distance <= 100) {
      setMyStoreData({ ...myStoreData, auto: true })
    } else {
      setMyStoreData({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeMyStore = useCallback(() => {
    removeLocalStorage(SESSION_MY_STORE_STRING)
  }, [])

  const renderMyStoreModalLocator = ({ product } = {}) => (
    <>
      {mapUiDrawerIsOpen && (
        <StoreLocatorProvider>
          <MyStoreModalLocator
            productInStore={product}
            isOpen={mapUiDrawerIsOpen}
            onClose={() => setMapUiDrawerIsOpen(false)}
          />
        </StoreLocatorProvider>
      )}
    </>
  )

  useEffect(() => {
    getMyStore()
  }, [getMyStore, lat, long, strapiStoreData])

  useEffect(() => {
    if (myStoreContext?.storeNumber) {
      saveLocalStorage(SESSION_MY_STORE_STRING, JSON.stringify(myStoreContext))
    }
  }, [myStoreContext])

  const getAllStrapiStores = useCallback(() => {
    setAllStrapiStores(strapiStoreData?.allStrapiStore?.edges.map(store => store.node))
  }, [strapiStoreData])

  const getAllStores = useCallback(() => {
    getAllStore().then(response => {
      const reviewsData = response
      setAllStores(reviewsData)
    })
  }, [])

  useEffect(() => {
    if (fetchProperties?.includes(FETCH_PROPERTIES.ALL_STORES)) {
      getAllStores()
    }
  }, [fetchProperties, getAllStores])

  useEffect(() => {
    getAllStrapiStores()
  }, [getAllStrapiStores])

  useEffect(() => {
    if (!_isEmpty(allStores)) {
      setAllStrapiStores(prevAllStrapiStores =>
        prevAllStrapiStores?.map(store => ({
          ...store,
          ...allStores.find(storeToFind => +store?.StoreNumber === +storeToFind?.storeNumber),
        })),
      )
      setStrapiStoresLoadedFlag(true)
    }
  }, [allStores])

  return {
    myStore,
    relatedStores,
    setMyStoreData,
    allStrapiStores,
    groupedStrapiStores,
    renderMyStoreModalLocator,
    mapUiDrawerIsOpen,
    setMapUiDrawerIsOpen,
    removeMyStore,
    strapiStoresLoadedFlag,
  }
}

const query = graphql`
  {
    allStrapiStore(filter: { strapi_id: { ne: 0 } }) {
      edges {
        node {
          id
          StoreNumber
          StoreName
          StoreType
          Address1
          Address2
          City
          State
          Zip
          Location {
            latLng {
              lat
              lng
            }
          }
          PhoneNumber
          StoreHours {
            mondayClosed
            mondayOpen
            tuesdayClosed
            tuesdayOpen
            wednesdayClosed
            wednesdayOpen
            thursdayClosed
            thursdayOpen
            fridayClosed
            fridayOpen
            saturdayClosed
            saturdayOpen
            sundayClosed
            sundayOpen
          }
        }
      }
    }
  }
`
